import { useState, useEffect } from "react";
import { api, apiError, API } from "@/lib/api";

function initialsOf(name = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

// Photo with graceful initials fallback (empty src, or image fails to load).
export function EntityPhoto({ src, name = "", className = "", rounded = "rounded-xl" }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);
  const show = src && !err;
  return (
    <div className={`flex items-center justify-center overflow-hidden bg-[#F3ECFF] font-bold text-[#6A38C7] ${rounded} ${className}`}>
      {show ? (
        <img src={src} alt={name} onError={() => setErr(true)} className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs">{initialsOf(name)}</span>
      )}
    </div>
  );
}

// Admin uploader for entity photo (entity = "staff" | "services"). Data save and photo
// upload are intentionally separate; this only calls the dedicated photo endpoints.
export function PhotoUploader({ entity, id, fotoId, name, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ts, setTs] = useState(Date.now());
  const src = fotoId ? `${API}/public/${entity}/${id}/foto?t=${ts}` : null;

  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    const okType = ["image/jpeg", "image/png", "image/webp"].includes(f.type) || /\.(jpe?g|png|webp)$/i.test(f.name);
    if (!okType) { setErr("Foto harus JPG, JPEG, PNG, atau WebP."); return; }
    if (f.size > 2 * 1024 * 1024) { setErr("Ukuran foto maksimal 2 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const r = await api.post(`/${entity}/${id}/foto`, fd);
      setTs(Date.now());
      onChange && onChange(r.data.foto_file_id);
    } catch (e2) { setErr(apiError(e2)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    setErr(""); setBusy(true);
    try { await api.delete(`/${entity}/${id}/foto`); onChange && onChange(null); }
    catch (e2) { setErr(apiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`photo-uploader-${entity}`}>
      <EntityPhoto src={src} name={name} className="h-16 w-16 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap gap-2">
          <label className={`cursor-pointer rounded-lg bg-[#EEE6FF] px-3 py-1.5 text-xs font-semibold text-[#6A38C7] ${busy ? "opacity-60" : ""}`} data-testid={`photo-pick-${entity}`}>
            {busy ? "Memproses..." : (fotoId ? "Ganti Foto" : "Unggah Foto")}
            <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} disabled={busy} data-testid={`photo-input-${entity}`} />
          </label>
          {fotoId && (
            <button type="button" onClick={del} disabled={busy} data-testid={`photo-del-${entity}`} className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 disabled:opacity-60">Hapus Foto</button>
          )}
        </div>
        {err && <p className="mt-1 text-xs text-rose-600" data-testid={`photo-err-${entity}`}>{err}</p>}
        <p className="mt-1 text-[11px] text-slate-400">JPG / PNG / WebP, maks 2 MB. Opsional.</p>
      </div>
    </div>
  );
}

export default EntityPhoto;
